import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import useSuggestions from '../hooks/use-suggestions.js';
import {useDebounce} from '../hooks/use-debounce.js';
import {useSearchHistory} from '../hooks/use-search-history.js';
import {KEYBOARD_FOCUS_RING_CLASSES, useKeyboardFocus} from '../hooks/use-keyboard-focus.js';
import {cleanSearchValue, isValidSearchValue} from '../../helper/search-helper.js';
import {
    SEARCH_INPUT_DELAY,
    SEARCH_LISTBOX_ID,
    SEARCH_QUERY_URL_PARAM,
    SEARCH_ORIGIN_QUERY_URL_PARAM,
} from '../navigation/constants.js';
import {SearchDropdown} from './SearchDropdown.jsx';
import {useBreakpointBetween, IconButton, Link} from 'bonprix-design-system';
import useSearchTexts from '@header/scripts/components/hooks/use-search-texts.js';
import usePopularSearches from '@header/scripts/components/hooks/use-popular-searches.js';
import {HeaderContext} from '@header/scripts/components/context/HeaderContext.jsx';
import {SearchTrinityEvents} from '../../helper/search-tracking.js';

const SEARCH_ORIGIN_SEARCH = 'textfield';
const SEARCH_ORIGIN_SUGGESTION = 'suggest';
const SEARCH_ORIGIN_HISTORY = 'lastsearch';
const SEARCH_ORIGIN_POPULAR = 'popularsearch';

const SUGGESTIONS_LIST_MODE = 'SUGGESTIONS';
const HISTORY_LIST_MODE = 'HISTORY';

const SearchField = ({searchUrl, hideLogo, showLogo}) => {
    const url = new URL(window.location.href);
    const params = url.searchParams;

    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState(
        params.get(SEARCH_ORIGIN_QUERY_URL_PARAM) || params.get(SEARCH_QUERY_URL_PARAM) || ''
    );
    const [isDropdownExpanded, setIsDropdownExpanded] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [selectionMode, setSelectionMode] = useState('HOVER');

    const deferredSearchTerm = useDebounce(searchTerm, SEARCH_INPUT_DELAY);
    const {recentSearches, addToSearchHistory, clearSearchHistory} = useSearchHistory();
    const isMobile = useBreakpointBetween('s', 'xl');
    const isLDesktop = useBreakpointBetween('xl', 'xxl'); // 992-1279px: 2 product suggestions
    const {data: searchTextsData} = useSearchTexts();
    const {data: suggestionsData} = useSuggestions(deferredSearchTerm);
    const {data: popularSearchesData} = usePopularSearches({enabled: isOpen && recentSearches.length === 0});
    const {headerLightMode} = useContext(HeaderContext);
    const keyboardFocus = useKeyboardFocus();

    // Popular searches are shown in the empty overlay only when there are no recent searches.
    const popularSearches = useMemo(() => (popularSearchesData || []).slice(0, 10), [popularSearchesData]);
    const showPopular = recentSearches.length === 0 && popularSearches.length > 0;

    const searchContainerRef = useRef(null);
    const inputRef = useRef(null);
    const triggerRef = useRef(null);
    const overlayHistoryRef = useRef(false);
    const isSelectingRef = useRef(false);
    const focusReleasedRef = useRef(false);
    const recentViewedRef = useRef(false);
    const popularViewedRef = useRef(false);

    const listMode = useMemo(() => (searchTerm.trim() ? SUGGESTIONS_LIST_MODE : HISTORY_LIST_MODE), [searchTerm]);



    useEffect(() => {
        setIsOpen(false);
        showLogo();
    }, [isMobile]);

    useEffect(() => {
        focusReleasedRef.current = false;
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            recentViewedRef.current = false;
            popularViewedRef.current = false;
            return;
        }
        if (listMode !== HISTORY_LIST_MODE) {
            return;
        }
        if (showPopular && !popularViewedRef.current) {
            popularViewedRef.current = true;
            SearchTrinityEvents.sendSearchPopularSearchesView(popularSearches);
        } else if (!showPopular && recentSearches.length > 0 && !recentViewedRef.current) {
            recentViewedRef.current = true;
            SearchTrinityEvents.sendSearchRecentSearchesView(recentSearches);
        }
    }, [isOpen, listMode, showPopular, recentSearches, popularSearches]);

    useEffect(() => {
        if (!isOpen || !isMobile) {
            return undefined;
        }
        window.history.pushState({searchOverlay: true}, '');
        overlayHistoryRef.current = true;
        const handlePopState = () => {
            overlayHistoryRef.current = false;
            resetOverlay();
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [isOpen, isMobile]);

    // Lock the background page while the full-screen mobile overlay is open. iOS Safari ignores
    // `overflow: hidden` on the body, so we pin the body with `position: fixed` (offset by the current
    // scroll position) and restore it on close. The `search-overlay-open` body class also lets pages
    // hide their own floating controls (PLP back-to-top, PDP sticky "add" bar) behind the overlay.
    useEffect(() => {
        if (!isOpen || !isMobile) {
            return undefined;
        }
        const {body} = document;
        const scrollY = window.scrollY;
        body.classList.add('search-overlay-open');
        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.left = '0';
        body.style.right = '0';
        body.style.width = '100%';
        return () => {
            body.classList.remove('search-overlay-open');
            body.style.position = '';
            body.style.top = '';
            body.style.left = '';
            body.style.right = '';
            body.style.width = '';
            window.scrollTo(0, scrollY);
        };
    }, [isOpen, isMobile]);

    const currentList = useMemo(() => {
        if (listMode === HISTORY_LIST_MODE) {
            // Empty overlay: recent searches take priority; popular searches are the fallback.
            return recentSearches.length > 0 ? recentSearches : popularSearches;
        }
        return Array.isArray(suggestionsData?.termSuggestions)
            ? suggestionsData.termSuggestions.filter((suggestion) => typeof suggestion?.value === 'string')
                .map((suggestion) => suggestion.value)
            : [];
    }, [listMode, recentSearches, popularSearches, suggestionsData]);

    const productSuggestions = useMemo(() => {
        if (listMode !== SUGGESTIONS_LIST_MODE) {
            return [];
        }
        const products = Array.isArray(suggestionsData?.productSuggestions)
            ? suggestionsData.productSuggestions.filter((product) => product?.link)
            : [];
        // Product count per breakpoint: mobile/tablet (<992) = 6, L (992-1279) = 2, XL+ (>=1280) = 3.
        const count = isMobile ? 6 : (isLDesktop ? 2 : 3);
        return products.slice(0, count);
    }, [listMode, suggestionsData, isMobile, isLDesktop]);

    const hasDropdownContent = currentList.length > 0 || productSuggestions.length > 0;
    const isListVisible = isOpen && listMode === SUGGESTIONS_LIST_MODE && currentList.length > 0;
    const suggestionQuery = suggestionsData?.searchTerm || '';
    const visibleSuggestionKey =
        listMode === SUGGESTIONS_LIST_MODE && isOpen && suggestionQuery.length > 0 && currentList.length > 0
            ? JSON.stringify(currentList)
            : '';
    useEffect(() => {
        if (visibleSuggestionKey && !isSelectingRef.current) {
            SearchTrinityEvents.sendSearchSuggestionTermView(currentList, suggestionQuery);
        }
    }, [visibleSuggestionKey]);

    const visibleProductKey =
        listMode === SUGGESTIONS_LIST_MODE && isOpen && suggestionQuery.length > 0 && productSuggestions.length > 0
            ? JSON.stringify(productSuggestions.map((product) => product.itemId))
            : '';
    useEffect(() => {
        if (visibleProductKey && !isSelectingRef.current) {
            SearchTrinityEvents.sendSearchSuggestionProductView(
                productSuggestions.map((product) => product.itemId),
                suggestionQuery,
            );
        }
    }, [visibleProductKey]);

    const handleProductSuggestionClick = (product) => {
        SearchTrinityEvents.sendSearchSuggestionProductClick(Number(product.itemId), searchTerm);
    };

    const handleSearchChange = (term) => {
        // The user is typing again — clear any leftover selection guard from a cancelled navigation.
        isSelectingRef.current = false;
        setSearchTerm(term);
        setSelectedIndex(-1);
    };

    const handleInputClick = () => {
        SearchTrinityEvents.sendSearchInputEnterTerm(searchTerm);
    };

    const handleFocus = () => {
        setIsDropdownExpanded(true);
    };

    const handleBlur = () => {
        setTimeout(() => {
            if (focusReleasedRef.current) {
                return;
            }
            const isSearchContainerFocussed = searchContainerRef.current?.contains(document.activeElement);
            if (!isSearchContainerFocussed) {
                setIsDropdownExpanded(false);
                setSelectedIndex(-1);
                if (isMobile) {
                    setIsOpen(false);
                    showLogo();
                }
            }
        }, 0);
    };

    // Android binds the soft keyboard to the focused element: hiding the keyboard leaves the search
    // field focused, so Chrome raises it again on the next tap that does not move the focus elsewhere —
    // and the dropdown's `onMouseDown={preventDefault}` (which keeps the overlay open, see `handleBlur`)
    // is exactly such a tap. Touching the reco module therefore drops the focus on purpose and marks the
    // blur as self-inflicted, so no field is left for the keyboard to pop up for while the overlay stays
    // open. Mouse pointers keep the untouched desktop behaviour.
    const handleRecommendationPointerDown = (e) => {
        if (e.pointerType === 'mouse' || document.activeElement !== inputRef.current) {
            return;
        }
        focusReleasedRef.current = true;
        inputRef.current.blur();
    };

    const handleKeyDown = (e) => {
        if (listMode !== SUGGESTIONS_LIST_MODE) {
            if (e.key === 'Enter') {
                submitTypedSearch();
            }
            return;
        }

        const listVisible = isDropdownExpanded && currentList.length > 0;

        const navigateNext = () => {
            if (!listVisible) {
                setIsDropdownExpanded(true);
                setSelectedIndex(0);
            } else {
                setSelectedIndex((prevIndex) => (prevIndex + 1) % currentList.length);
            }
        };

        const navigatePrevious = () => {
            if (listVisible) {
                setSelectedIndex((prevIndex) => (prevIndex - 1 + currentList.length) % currentList.length);
            }
        };

        const selectItem = () => {
            if (selectedIndex >= 0 && listVisible) {
                e.preventDefault();
                const selectedItem = currentList[selectedIndex];
                handleSelectItem(selectedItem);
            } else {
                submitTypedSearch();
            }
        };

        const closeList = () => {
            setIsDropdownExpanded(false);
            setSelectedIndex(-1);
        };

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectionMode('SELECT');
                navigateNext();
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectionMode('SELECT');
                navigatePrevious();
                break;
            case 'Enter':
                selectItem();
                break;
            case 'Tab':
                if (e.shiftKey) {
                    if (selectedIndex <= 0 || !listVisible) {
                        closeList();
                        return;
                    }
                    e.preventDefault();
                    setSelectionMode('SELECT');
                    navigatePrevious();
                    return;
                } else {
                    if (selectedIndex === currentList.length - 1) {
                        closeList();
                        return;
                    }
                    e.preventDefault();
                    setSelectionMode('SELECT');
                    navigateNext();
                }
                break;
            default:
                break;
        }
    };

    const handleSelectItem = (value) => {
        isSelectingRef.current = true;
        setSearchTerm(value);
        let origin = SEARCH_ORIGIN_SEARCH;

        if (listMode === SUGGESTIONS_LIST_MODE) {
            origin = SEARCH_ORIGIN_SUGGESTION;
            SearchTrinityEvents.sendSearchSuggestionTermClick(value, searchTerm, productSuggestions.length);
        } else if (listMode === HISTORY_LIST_MODE) {
            origin = showPopular ? SEARCH_ORIGIN_POPULAR : SEARCH_ORIGIN_HISTORY;
        }

        setIsDropdownExpanded(false);
        setSelectedIndex(-1);
        performSearch(value, origin);
    };

    function handleSearchResultItemHover(index) {
        setSelectedIndex(index);
        setSelectionMode('HOVER');
    }

    function unselectField() {
        setSelectedIndex(-1);
    }

    const handleClearSearch = () => {
        SearchTrinityEvents.sendSearchInputDeleteTerm(searchTerm);
        setSearchTerm('');
        setSelectedIndex(-1);
        inputRef.current?.focus();
    };

    // Recent-search tag: the search-form-arrow inserts the term into the field and re-activates it
    // (without running the search — clicking the term itself runs the search via handleSelectItem).
    const handleInsertTerm = (term) => {
        setSearchTerm(term);
        setSelectedIndex(-1);
        setIsDropdownExpanded(true);
        inputRef.current?.focus();
    };

    const resetOverlay = () => {
        setIsDropdownExpanded(false);
        setSelectedIndex(-1);
        setIsOpen(false);
        if (isMobile) {
            showLogo();
        }
    };

    const closeOverlay = () => {
        if (overlayHistoryRef.current) {
            overlayHistoryRef.current = false;
            window.history.back();
            return;
        }
        resetOverlay();
    };

    const closeAndRestoreFocus = () => {
        closeOverlay();
        // Without this the overlay closes with focus on `<body>`, so the next Tab restarts at the top
        // of the page instead of resuming where the shopper was.
        triggerRef.current?.querySelector('button')?.focus({preventScroll: true});
    };

    // Document-level, not on the input: focus can sit on a chip, a product suggestion, the close
    // button or inside the recommendation carousel, and Escape has to close from all of them.
    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                closeAndRestoreFocus();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, isMobile]);

    // Tabbing past the last control in the overlay means the shopper has moved on, and with no focus
    // trap the overlay would otherwise stay open over the page while focus is somewhere behind it.
    const handleContainerBlur = (e) => {
        handleBlur();
        const next = e.relatedTarget;
        // A null `relatedTarget` means focus left the document altogether (tab switch, devtools, the
        // browser chrome) or went nowhere focusable (a click on plain page content) — the overlay
        // survives that; the scrim and the close button handle pointer dismissal.
        if (!isOpen || !next || searchContainerRef.current?.contains(next)) {
            return;
        }
        closeOverlay();
    };

    const handleViewAllResults = () => {
        SearchTrinityEvents.sendSearchLayerAllResults();
        performSearch(searchTerm, SEARCH_ORIGIN_SEARCH);
    };

    const handleButtonClick = (e) => {
        e.preventDefault();
        SearchTrinityEvents.sendSearchIconClick();
        if (isOpen) {
            submitTypedSearch();
        } else {
            setIsOpen(true);
            SearchTrinityEvents.sendSearchLayerView();
            if (isMobile) {
                hideLogo();
            }
        }
    };

    const performSearch = (passedSearchTerm, searchOrigin) => {
        const cleanedSearchValue = cleanSearchValue(passedSearchTerm);
        if (cleanedSearchValue.length === 0 || !isValidSearchValue(cleanedSearchValue)) {
            return;
        }

        inputRef.current.value = cleanedSearchValue;

        setSearchTerm(cleanedSearchValue);

        if (searchOrigin !== SEARCH_ORIGIN_HISTORY) {
            addToSearchHistory(cleanedSearchValue);
        }
        window.location.href = `${searchUrl}?${SEARCH_QUERY_URL_PARAM}=${encodeURIComponent(cleanedSearchValue)}&searchmode=m&search_origin=${searchOrigin}`;
    };

    const submitTypedSearch = () => {
        const cleanedSearchValue = cleanSearchValue(searchTerm);
        if (cleanedSearchValue.length === 0 || !isValidSearchValue(cleanedSearchValue)) {
            return;
        }
        SearchTrinityEvents.sendSearchInputSubmit(cleanedSearchValue);
        performSearch(searchTerm, SEARCH_ORIGIN_SEARCH);
    };

    // One trigger, two jobs (see `handleButtonClick`): while the overlay is closed the button opens
    // it, once open it submits the typed term — so its accessible name has to follow that switch.
    const searchButtonLabel = isOpen ? 'Suche abschicken' : 'Suche öffnen';

    const textColor = `text-clr-${headerLightMode ? 'on-primary-high' : 'primary'}`;

    return (
        <>
            {isOpen && (
                <div
                    className="search__scrim"
                    aria-hidden="true"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                        SearchTrinityEvents.sendSearchLayerCloseBackground();
                        closeOverlay();
                    }}
                />
            )}
        <div
            ref={searchContainerRef}
            className={`search__container${isOpen ? ' -open' : ''}${headerLightMode ? ' -light' : ''}`}
            onBlur={handleContainerBlur}
        >
            <div className="search__panel">
            <div className="search__bar px-4 xl:px-0">
                <div className="search__field-group">
                    <div className="search__input-field-wrapper">
                        <input
                            id={'search'}
                            ref={inputRef}
                            type="text"
                            inputMode="search"
                            enterKeyHint="search"
                            className={`search__input-field ${textColor} ${keyboardFocus.isKeyboardFocus ? KEYBOARD_FOCUS_RING_CLASSES : ''} rounded`}
                            placeholder=" "
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            // capture props, so that the ring is tracked without touching the focus/blur behaviour above
                            onFocusCapture={keyboardFocus.onFocusCapture}
                            onBlurCapture={keyboardFocus.onBlurCapture}
                            onClick={handleInputClick}
                            autoComplete="off"
                            value={searchTerm}
                            onKeyDown={handleKeyDown}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            role="combobox"
                            aria-expanded={isListVisible}
                            aria-controls={SEARCH_LISTBOX_ID}
                            aria-autocomplete="list"
                            aria-activedescendant={
                                isListVisible && selectedIndex >= 0 ? `search-option-${selectedIndex}` : undefined
                            }
                        />
                        <label htmlFor={'search'} className="search__input-field-label text-clr-on-bg-low">
                            {searchTextsData?.searchPlaceholder || 'Wonach suchst Du?'}
                        </label>
                    </div>
                    {searchTerm && (isOpen || isDropdownExpanded) && (
                        <span className="search__clear" onMouseDown={(e) => e.preventDefault()}>
                            <Link small component="button" onClick={handleClearSearch}>
                                {searchTextsData?.clearSearch || 'löschen'}
                            </Link>
                        </span>
                    )}
                    <span className="search__submit" ref={triggerRef} onMouseDown={(e) => e.preventDefault()}>
                        <IconButton
                            icon="search"
                            label={searchButtonLabel}
                            variant={searchTerm && isOpen ? 'primary' : 'tertiary'}
                            onClick={handleButtonClick}
                        />
                    </span>
                </div>
                <span className="search__close" onMouseDown={(e) => e.preventDefault()}>
                    <IconButton
                        icon="closer"
                        label={searchTextsData?.closeButtonLabel || 'Suche schließen'}
                        variant="tertiary"
                        onClick={() => {
                            SearchTrinityEvents.sendSearchLayerCloseX();
                            closeOverlay();
                        }}
                    />
                </span>
            </div>

            {isOpen && hasDropdownContent && (
                <SearchDropdown
                    listMode={listMode}
                    currentList={currentList}
                    productSuggestions={productSuggestions}
                    deferredSearchTerm={deferredSearchTerm}
                    selectedIndex={selectedIndex}
                    selectionMode={selectionMode}
                    handleSelectItem={handleSelectItem}
                    handleSearchResultItemHover={handleSearchResultItemHover}
                    handleViewAllResults={handleViewAllResults}
                    handleProductSuggestionClick={handleProductSuggestionClick}
                    handleInsertTerm={handleInsertTerm}
                    onRecommendationPointerDown={handleRecommendationPointerDown}
                    unselectField={unselectField}
                    clearSearchHistory={clearSearchHistory}
                    showPopular={showPopular}
                    popularSearches={popularSearches}
                    popularHeader={searchTextsData?.popularSearchesHeader}
                    showMoreLabel={searchTextsData?.showMoreRecentSearches}
                    showLessLabel={searchTextsData?.showLessRecentSearches}
                />
            )}
            </div>
        </div>
        </>
    );
};

export default SearchField;
